import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiX, FiLink, FiCalendar, FiPlus, FiChevronDown, FiArrowDown } from 'react-icons/fi';
import { FormLabel } from '../../ui/FormLabel';

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;

  title: string;
  setTitle: (val: string) => void;

  description: string;
  setDescription: (val: string) => void;

  link: string;
  setLink: (val: string) => void;

  coverFileName: string;
  setCoverFile: (file: File | null) => void;
  setCoverFileName: (name: string) => void;

  attachmentFileName: string;
  setAttachmentFile: (file: File | null) => void;
  setAttachmentFileName: (name: string) => void;

  startDate: string;
  setStartDate: (val: string) => void;

  endDate: string;
  setEndDate: (val: string) => void;

  assigneeId: string;
  setAssigneeId: (val: string) => void;
  membersData?: any;

  quantity: string;
  setQuantity: (val: string) => void;

  budgetAllocation: string;
  setBudgetAllocation: (val: string) => void;

  priority: 'Low' | 'Medium' | 'High';
  setPriority: (val: 'Low' | 'Medium' | 'High') => void;

  status: 'To Do' | 'In Progress' | 'Blocked' | 'Complete';
  setStatus: (val: 'To Do' | 'In Progress' | 'Blocked' | 'Complete') => void;

  errors: Record<string, string | undefined>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>;
}

export const AddTaskModal: React.FC<AddTaskModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  title,
  setTitle,
  description,
  setDescription,
  link,
  setLink,
  coverFileName,
  setCoverFile,
  setCoverFileName,
  attachmentFileName,
  setAttachmentFile,
  setAttachmentFileName,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  assigneeId,
  setAssigneeId,
  membersData,
  quantity,
  setQuantity,
  budgetAllocation,
  setBudgetAllocation,
  priority,
  setPriority,
  status,
  setStatus,
  errors,
  setErrors,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-[calc(100vw-32px)] sm:w-full max-w-lg bg-[#0B0B0B] border border-white/10 rounded-[28px] p-5 sm:p-6 text-left flex flex-col max-h-[82vh] z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)] overflow-hidden"
          >
            {/* Sticky Header */}
            <div className="shrink-0 flex items-start justify-between pb-4 mb-4 border-b border-white/5 bg-[#0B0B0B] z-20">
              <div>
                <h2 className="text-xl font-bold text-white">Create Task</h2>
                <p className="text-zinc-550 text-xs mt-1">
                  Assign work, track progress, and keep your project on schedule.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden relative">
              {/* Scrollable Body */}
              <div className="flex-1 overflow-y-auto custom-modal-scrollbar space-y-4 pr-2.5">
                {/* Scroll Down Visual Hint Pill */}
                <div className="flex items-center justify-between gap-2 px-3.5 py-2 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-300 text-[11px] font-medium">
                  <div className="flex items-center gap-2">
                    <FiArrowDown className="w-3.5 h-3.5 text-blue-400 animate-bounce shrink-0" />
                    <span>Scroll down to access dates, crew, budget & status</span>
                  </div>
                  <span className="text-[10px] uppercase font-bold text-blue-400/80 tracking-wider shrink-0 hidden sm:inline">
                    Scrollable Form
                  </span>
                </div>

                {/* Title */}
                <div>
                  <FormLabel required className="text-zinc-400 text-xs font-semibold mb-2">
                    Title
                  </FormLabel>
                  <input
                    type="text"
                    placeholder="e.g. Confirm lighting vendor"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
                    }}
                    className={`w-full bg-[#0F0F0F] border outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all ${
                      errors.title
                        ? 'border-red-500/80 focus:border-red-500'
                        : 'border-white/5 hover:border-white/10 focus:border-white/20'
                    }`}
                  />
                  {errors.title && <p className="text-red-400 text-xs mt-1">{errors.title}</p>}
                </div>

                {/* Description */}
                <div>
                  <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                    Description
                  </FormLabel>
                  <textarea
                    placeholder="Describe what needs to be done..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-white/20 outline-none text-white text-xs rounded-xl px-4 py-3.5 resize-none transition-all"
                  />
                </div>

                {/* Upload Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Cover image zone */}
                  <div>
                    <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                      Image
                    </FormLabel>
                    <input
                      type="file"
                      id="cover-file-input"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setCoverFile(file);
                          setCoverFileName(file.name);
                          if (errors.coverFile)
                            setErrors((prev) => ({ ...prev, coverFile: undefined }));
                        }
                      }}
                    />
                    {coverFileName ? (
                      <div className="relative w-full border border-blue-500/30 bg-blue-950/20 rounded-2xl p-3.5 flex items-center justify-between gap-2.5 min-h-[90px]">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className="w-8 h-8 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center shrink-0 text-blue-400">
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] uppercase font-bold text-blue-400 tracking-wider">
                              Image
                            </p>
                            <p className="text-xs text-zinc-200 font-semibold truncate mt-0.5">
                              {coverFileName}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          title="Remove image"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCoverFile(null);
                            setCoverFileName('');
                            const el = document.getElementById(
                              'cover-file-input',
                            ) as HTMLInputElement | null;
                            if (el) el.value = '';
                          }}
                          className="p-1.5 rounded-full bg-white/10 hover:bg-red-500/20 text-zinc-400 hover:text-red-400 transition-all cursor-pointer shrink-0"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => document.getElementById('cover-file-input')?.click()}
                        className={`w-full border border-dashed rounded-2xl p-4 flex flex-col items-center justify-center text-center cursor-pointer transition-all min-h-[90px] ${
                          errors.coverFile
                            ? 'border-red-500/80 bg-red-950/10'
                            : 'border-white/10 hover:border-white/20 bg-[#0F0F0F]/65 hover:bg-white/[0.01]'
                        }`}
                      >
                        <svg
                          className="w-5 h-5 text-zinc-500 mb-1.5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <p className="text-[11px] text-zinc-300 font-semibold leading-normal truncate max-w-full px-2">
                          Upload image — <span className="text-blue-500">browse</span>
                        </p>
                        <p className="text-[9px] text-zinc-550 mt-0.5">PNG, JPG up to 5 MB</p>
                      </button>
                    )}
                    {errors.coverFile && (
                      <p className="text-red-400 text-xs mt-1">{errors.coverFile}</p>
                    )}
                  </div>

                  {/* Attachments zone */}
                  <div>
                    <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                      Attachments
                    </FormLabel>
                    <input
                      type="file"
                      id="attach-file-input"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setAttachmentFile(file);
                          setAttachmentFileName(file.name);
                          if (errors.attachmentFile)
                            setErrors((prev) => ({ ...prev, attachmentFile: undefined }));
                        }
                      }}
                    />
                    {attachmentFileName ? (
                      <div className="relative w-full border border-blue-500/30 bg-blue-950/20 rounded-2xl p-3.5 flex items-center justify-between gap-2.5 min-h-[90px]">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className="w-8 h-8 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center shrink-0 text-blue-400">
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] uppercase font-bold text-blue-400 tracking-wider">
                              Attachment
                            </p>
                            <p className="text-xs text-zinc-200 font-semibold truncate mt-0.5">
                              {attachmentFileName}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          title="Remove attachment"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAttachmentFile(null);
                            setAttachmentFileName('');
                            const el = document.getElementById(
                              'attach-file-input',
                            ) as HTMLInputElement | null;
                            if (el) el.value = '';
                          }}
                          className="p-1.5 rounded-full bg-white/10 hover:bg-red-500/20 text-zinc-400 hover:text-red-400 transition-all cursor-pointer shrink-0"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => document.getElementById('attach-file-input')?.click()}
                        className={`w-full border border-dashed rounded-2xl p-4 flex flex-col items-center justify-center text-center cursor-pointer transition-all min-h-[90px] ${
                          errors.attachmentFile
                            ? 'border-red-500/80 bg-red-950/10'
                            : 'border-white/10 hover:border-white/20 bg-[#0F0F0F]/65 hover:bg-white/[0.01]'
                        }`}
                      >
                        <svg
                          className="w-5 h-5 text-zinc-500 mb-1.5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <p className="text-[11px] text-zinc-300 font-semibold leading-normal truncate max-w-full px-2">
                          Attach files — <span className="text-blue-500">browse</span>
                        </p>
                        <p className="text-[9px] text-zinc-550 mt-0.5">Files up to 20 MB</p>
                      </button>
                    )}
                    {errors.attachmentFile && (
                      <p className="text-red-400 text-xs mt-1">{errors.attachmentFile}</p>
                    )}
                  </div>
                </div>

                {/* External Link */}
                <div>
                  <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                    External Link
                  </FormLabel>
                  <div className="relative">
                    <FiLink className="w-4 h-4 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                      type="url"
                      placeholder="https://drive.google.com/..."
                      value={link}
                      onChange={(e) => {
                        setLink(e.target.value);
                        if (errors.externalLink)
                          setErrors((prev) => ({ ...prev, externalLink: undefined }));
                      }}
                      className={`w-full bg-[#0F0F0F] border outline-none text-white text-xs rounded-xl pl-11 pr-4 py-3.5 transition-all ${
                        errors.externalLink
                          ? 'border-red-500/80 focus:border-red-500'
                          : 'border-white/5 hover:border-white/10 focus:border-white/20'
                      }`}
                    />
                  </div>
                  {errors.externalLink && (
                    <p className="text-red-400 text-xs mt-1">{errors.externalLink}</p>
                  )}
                </div>

                {/* Start Date & End Date */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                      Start Date
                    </FormLabel>
                    <div className="relative">
                      <FiCalendar className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (errors.startDate || errors.endDate) {
                            setErrors((prev) => ({
                              ...prev,
                              startDate: undefined,
                              endDate: undefined,
                            }));
                          }
                        }}
                        className={`w-full bg-[#0F0F0F] border outline-none text-white text-xs rounded-xl pl-10 pr-3 py-3 sm:py-3.5 transition-all [color-scheme:dark] ${
                          errors.startDate
                            ? 'border-red-500/80 focus:border-red-500'
                            : 'border-white/5 hover:border-white/10 focus:border-white/20'
                        }`}
                      />
                    </div>
                    {errors.startDate && (
                      <p className="text-red-400 text-xs mt-1">{errors.startDate}</p>
                    )}
                  </div>
                  <div>
                    <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                      End Date
                    </FormLabel>
                    <div className="relative">
                      <FiCalendar className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          if (errors.endDate)
                            setErrors((prev) => ({ ...prev, endDate: undefined }));
                        }}
                        className={`w-full bg-[#0F0F0F] border outline-none text-white text-xs rounded-xl pl-10 pr-3 py-3 sm:py-3.5 transition-all [color-scheme:dark] ${
                          errors.endDate
                            ? 'border-red-500/80 focus:border-red-500'
                            : 'border-white/5 hover:border-white/10 focus:border-white/20'
                        }`}
                      />
                    </div>
                    {errors.endDate && (
                      <p className="text-red-400 text-xs mt-1">{errors.endDate}</p>
                    )}
                  </div>
                </div>

                {/* Assignee */}
                <div>
                  <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                    Assignee
                  </FormLabel>
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-400">
                      <FiPlus className="w-4 h-4" />
                    </div>
                    <select
                      value={assigneeId}
                      onChange={(e) => setAssigneeId(e.target.value)}
                      className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-white/20 outline-none text-white text-xs rounded-xl pl-13 pr-10 py-3.5 appearance-none cursor-pointer transition-all"
                    >
                      <option value="" className="bg-zinc-900 text-zinc-400">
                        Select crew member
                      </option>
                      {membersData?.results?.map((m: any) => {
                        const memberUser = m.user || m.member || m;
                        const name =
                          memberUser.full_name ||
                          memberUser.username ||
                          memberUser.email ||
                          `Member ${m.id}`;
                        return (
                          <option
                            key={m.id || memberUser.id}
                            value={memberUser.id || m.id}
                            className="bg-zinc-900 text-white"
                          >
                            {name} ({m.role || 'Member'})
                          </option>
                        );
                      })}
                    </select>
                    <FiChevronDown className="w-4 h-4 text-zinc-500 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>

                {/* Quantity & Budget Allocation (Optional) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                      Quantity
                    </FormLabel>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => {
                        setQuantity(e.target.value);
                        if (errors.quantity)
                          setErrors((prev) => ({ ...prev, quantity: undefined }));
                      }}
                      className={`w-full bg-[#0F0F0F] border outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all ${
                        errors.quantity
                          ? 'border-red-500/80 focus:border-red-500'
                          : 'border-white/5 hover:border-white/10 focus:border-white/20'
                      }`}
                    />
                    {errors.quantity && (
                      <p className="text-red-400 text-xs mt-1">{errors.quantity}</p>
                    )}
                  </div>
                  <div>
                    <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                      Budget Allocation
                    </FormLabel>
                    <input
                      type="text"
                      placeholder="0.00"
                      value={budgetAllocation}
                      onChange={(e) => {
                        setBudgetAllocation(e.target.value);
                        if (errors.budgetAllocation)
                          setErrors((prev) => ({ ...prev, budgetAllocation: undefined }));
                      }}
                      className={`w-full bg-[#0F0F0F] border outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all ${
                        errors.budgetAllocation
                          ? 'border-red-500/80 focus:border-red-500'
                          : 'border-white/5 hover:border-white/10 focus:border-white/20'
                      }`}
                    />
                    {errors.budgetAllocation && (
                      <p className="text-red-400 text-xs mt-1">{errors.budgetAllocation}</p>
                    )}
                  </div>
                </div>

                {/* PRIORITY (Optional) */}
                <div>
                  <FormLabel
                    optional
                    className="text-zinc-400 text-[11px] font-bold tracking-wider uppercase mb-2"
                  >
                    PRIORITY
                  </FormLabel>
                  <div className="flex items-center gap-3">
                    {(['Low', 'Medium', 'High'] as const).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPriority(p)}
                        className={`px-5 py-2 rounded-full text-xs font-bold border transition-all cursor-pointer ${
                          priority === p
                            ? p === 'Low'
                              ? 'bg-emerald-950/40 border-emerald-500/50 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                              : p === 'Medium'
                                ? 'bg-amber-950/40 border-amber-500/50 text-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.15)]'
                                : 'bg-rose-950/40 border-rose-500/50 text-rose-400 shadow-[0_0_15px_rgba(244,63,94,0.15)]'
                            : 'bg-[#0F0F0F] text-zinc-400 border-white/10 hover:border-white/20'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* STATUS (Optional) */}
                <div>
                  <FormLabel
                    optional
                    className="text-zinc-400 text-[11px] font-bold tracking-wider uppercase mb-2"
                  >
                    STATUS
                  </FormLabel>
                  <div className="flex flex-wrap items-center gap-3">
                    {(['To Do', 'In Progress', 'Blocked', 'Complete'] as const).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStatus(s)}
                        className={`px-5 py-2 rounded-full text-xs font-bold border transition-all cursor-pointer ${
                          status === s
                            ? s === 'To Do'
                              ? 'bg-transparent border-white text-white shadow-md'
                              : s === 'In Progress'
                                ? 'bg-blue-600/30 border-blue-500 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.15)]'
                                : s === 'Blocked'
                                  ? 'bg-rose-950/50 border-rose-800 text-rose-400 shadow-[0_0_15px_rgba(225,29,72,0.15)]'
                                  : 'bg-emerald-950/50 border-emerald-500 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                            : 'bg-[#0F0F0F] text-zinc-400 border-white/10 hover:border-white/20'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="shrink-0 flex items-center justify-center pt-4 mt-4 border-t border-white/5 bg-[#0B0B0B] z-20">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto min-w-[200px] px-8 py-3 bg-white text-black hover:bg-zinc-100 rounded-full text-xs font-bold cursor-pointer transition-all active:scale-95 shadow-xl shadow-white/5 ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Uploading & Creating...' : 'Create Task'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
